import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  DoubleMaterialityValueChainPart,
  DoubleMaterialityESRSAreaEnum,
} from "@green0meter/api-client";
import type { ControllerRenderProps, FieldError } from "react-hook-form";

import { useESRSLabels } from "../../../Stakeholders/components/useESRSLabels";
import type { AssessmentFormValues } from "./AssessmentEditor.types";
import { useESRSTopics } from "./useESRSTopics";

import { FormControl, Select, MenuItem, Typography } from "@components/base";

export const useValueChainLabels = (): {
  getValueChainLabel: (part: DoubleMaterialityValueChainPart) => string;
} => {
  const { t } = useTranslation();

  const getValueChainLabel = (part: DoubleMaterialityValueChainPart): string => {
    const labels: Record<DoubleMaterialityValueChainPart, string> = {
      [DoubleMaterialityValueChainPart.Upstream]: t(
        "valueChain.upstream",
        "Upstream",
      ),
      [DoubleMaterialityValueChainPart.OwnOperations]: t(
        "valueChain.ownOperations",
        "Own Operations",
      ),
      [DoubleMaterialityValueChainPart.Downstream]: t(
        "valueChain.downstream",
        "Downstream",
      ),
    };

    return labels[part];
  };

  return { getValueChainLabel };
};

export type SubSubTopic = {
  code: string;
  name: string;
};

export type Subtopic = {
  code: string;
  name: string;
  // not a typo subSub is what we call it
  subSubTopics?: readonly SubSubTopic[];
};

export const TopicSelect: React.FC<{
  field: ControllerRenderProps<AssessmentFormValues, "topic">;
  error?: FieldError;
  disabled?: boolean;
}> = ({ field, error, disabled }) => {
  const { TOPIC_OPTIONS } = useESRSTopics();

  return (
    <FormControl fullWidth error={Boolean(error)}>
      <Select {...field} disabled={Boolean(disabled)} variant="filled" fullWidth>
        {TOPIC_OPTIONS.map((topic) => (
          <MenuItem key={topic.code} value={topic.code}>
            {topic.name}
          </MenuItem>
        ))}
      </Select>
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
          {error.message}
        </Typography>
      )}
    </FormControl>
  );
};

export const renderTopicSelect = (
  field: ControllerRenderProps<AssessmentFormValues, "topic">,
  error?: FieldError,
  disabled?: boolean,
): React.ReactElement => (
  <TopicSelect field={field} error={error} disabled={disabled} />
);

export const renderSubTopicSelect = (
  field: ControllerRenderProps<AssessmentFormValues, "subTopic">,
  error?: FieldError,
  disabled?: boolean,
  selectedTopic?: DoubleMaterialityESRSAreaEnum | null,
): React.ReactElement => {
  const { getSubtopicsForTopic } = useESRSTopics();
  const hasTopicSelected = selectedTopic !== null && selectedTopic !== undefined;
  const subtopics = useMemo(
    () => (hasTopicSelected ? getSubtopicsForTopic(selectedTopic) : []),
    [hasTopicSelected, selectedTopic, getSubtopicsForTopic],
  );
  const hasSubtopics = subtopics.length > 0;

  return (
    <FormControl fullWidth error={Boolean(error)}>
      <Select
        {...field}
        disabled={Boolean(disabled) || !hasTopicSelected || !hasSubtopics}
        variant="filled"
        fullWidth
        value={!hasTopicSelected || !hasSubtopics ? "" : (field.value ?? "")}
      >
        {!hasTopicSelected ? (
          <MenuItem value="" disabled>
            {"Select a topic first"}
          </MenuItem>
        ) : !hasSubtopics ? (
          <MenuItem value="" disabled>
            {"No sub-topics available"}
          </MenuItem>
        ) : (
          subtopics.map((subtopic) => (
            <MenuItem key={subtopic.code} value={subtopic.code}>
              {subtopic.name}
            </MenuItem>
          ))
        )}
      </Select>
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
          {error.message}
        </Typography>
      )}
    </FormControl>
  );
};

export const renderSubSubTopicSelect = (
  field: ControllerRenderProps<AssessmentFormValues, "subSubTopic">,
  error?: FieldError,
  disabled?: boolean,
  selectedTopic?: DoubleMaterialityESRSAreaEnum | null,
  selectedSubTopic?: string | null,
): React.ReactElement => {
  const { getSubSubtopicsForTopic } = useESRSTopics();
  const subSubTopics = useMemo(
    () =>
      selectedTopic !== null && selectedTopic !== undefined && selectedSubTopic
        ? getSubSubtopicsForTopic(selectedTopic, selectedSubTopic)
        : [],
    [selectedTopic, selectedSubTopic, getSubSubtopicsForTopic],
  );
  const hasSubSubTopics = subSubTopics.length > 0;
  const hasTopicSelected = selectedTopic !== null && selectedTopic !== undefined;

  return (
    <FormControl fullWidth error={Boolean(error)}>
      <Select
        {...field}
        disabled={
          Boolean(disabled) ||
          !hasTopicSelected ||
          !selectedSubTopic ||
          !hasSubSubTopics
        }
        variant="filled"
        fullWidth
        value={
          !hasTopicSelected || !selectedSubTopic || !hasSubSubTopics
            ? ""
            : (field.value ?? "")
        }
      >
        {!hasTopicSelected ? (
          <MenuItem value="" disabled>
            {"Select a topic first"}
          </MenuItem>
        ) : !selectedSubTopic ? (
          <MenuItem value="" disabled>
            {"Select a sub-topic first"}
          </MenuItem>
        ) : !hasSubSubTopics ? (
          <MenuItem value="" disabled>
            {"No sub-sub-topics available"}
          </MenuItem>
        ) : (
          subSubTopics.map((subSubTopic) => (
            <MenuItem key={subSubTopic.code} value={subSubTopic.code}>
              {subSubTopic.name}
            </MenuItem>
          ))
        )}
      </Select>
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
          {error.message}
        </Typography>
      )}
    </FormControl>
  );
};

type EsrsSelectProps = {
  field: ControllerRenderProps<
    AssessmentFormValues,
    "topic" | "subTopic" | "subSubTopic"
  >;
  error?: FieldError;
  disabled?: boolean;
};

const ESRSSelectField: React.FC<EsrsSelectProps> = ({ field, error, disabled }) => {
  const { ESRS_OPTIONS } = useESRSLabels();

  return (
    <FormControl fullWidth error={Boolean(error)}>
      <Select {...field} disabled={Boolean(disabled)} variant="filled" fullWidth>
        {ESRS_OPTIONS.map((option) => (
          <MenuItem key={option.code} value={option.enumValue}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
          {error.message}
        </Typography>
      )}
    </FormControl>
  );
};

export const renderESRSSelect = (
  field: ControllerRenderProps<
    AssessmentFormValues,
    "topic" | "subTopic" | "subSubTopic"
  >,
  error?: FieldError,
  disabled?: boolean,
): React.ReactElement => (
  <ESRSSelectField field={field} error={error} disabled={disabled} />
);
